import * as React from "react"
import Link from "next/link"
import Image from "next/image"

import { MainNavItem } from "@/types"
import { siteConfig } from "@/config/site"
import { cn } from "@/lib/utils"
import { useLockBody } from "@/hooks/use-lock-body"
import { Icons } from "@/components/icons"

/* ─── Icon + accent colour per nav label ─── */
const NAV_META: Record<
  string,
  { icon: keyof typeof Icons; color: string; description: string }
> = {
  "Featured On": {
    icon: "sparkles",
    color: "text-amber-600 bg-amber-50 ring-amber-200/60",
    description: "See where we've been featured",
  },
  Demo: {
    icon: "playcircle",
    color: "text-indigo-600 bg-indigo-50 ring-indigo-200/60",
    description: "Try our live chatbot",
  },
  Pricing: {
    icon: "zap",
    color: "text-emerald-600 bg-emerald-50 ring-emerald-200/60",
    description: "Plans that scale with you",
  },
  FAQ: {
    icon: "help",
    color: "text-violet-600 bg-violet-50 ring-violet-200/60",
    description: "Common questions answered",
  },
  Templates: {
    icon: "layoutTemplate",
    color: "text-pink-600 bg-pink-50 ring-pink-200/60",
    description: "Pre-built chatbot templates",
  },
  Careers: {
    icon: "user",
    color: "text-sky-600 bg-sky-50 ring-sky-200/60",
    description: "Join our growing team",
  },
}

interface MobileNavProps {
  items: MainNavItem[]
  children?: React.ReactNode
  onClose?: () => void
}

export function MobileNav({ items, children, onClose }: MobileNavProps) {
  useLockBody()

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] md:hidden animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer — slides from right */}
      <div
        className={cn(
          "fixed inset-y-0 right-0 z-50 flex w-[300px] max-w-[85vw] flex-col",
          "bg-white shadow-2xl shadow-slate-900/10",
          "animate-in slide-in-from-right duration-300 ease-out md:hidden"
        )}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <Link href="/" onClick={onClose} className="flex items-center gap-2">
            <Image
              src="/botbyte-logo.png"
              alt={siteConfig.name}
              width={200}
              height={90}
              className="h-16 w-auto object-contain"
            />
          </Link>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close menu"
          >
            <Icons.close className="h-4 w-4" />
          </button>
        </div>

        {/* ── Nav links ── */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="grid gap-0.5">
            {items.map((item, index) => {
              const meta = NAV_META[item.title]
              const IconComp = meta ? Icons[meta.icon] : Icons.arrowRight
              const colorCls = meta?.color ?? "text-slate-500 bg-slate-50 ring-slate-200/60"
              const desc = meta?.description

              return (
                <Link
                  key={index}
                  href={item.disabled ? "#" : item.href}
                  onClick={onClose}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl px-3 py-3 transition-all duration-150",
                    "hover:bg-slate-50 active:scale-[0.98]",
                    item.disabled && "pointer-events-none opacity-40"
                  )}
                  style={{ animationDelay: `${index * 40}ms` }}
                >
                  <span
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 transition-transform group-hover:scale-105",
                      colorCls
                    )}
                  >
                    <IconComp className="h-[18px] w-[18px]" />
                  </span>
                  <div className="flex min-w-0 flex-col">
                    <span className="text-[13px] font-semibold text-slate-800">
                      {item.title}
                    </span>
                    {desc && (
                      <span className="truncate text-[11px] text-slate-400">
                        {desc}
                      </span>
                    )}
                  </div>
                  <Icons.arrowRight className="ml-auto h-3 w-3 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )
            })}
          </div>

          {children && (
            <div className="mt-3 border-t border-slate-100 pt-3">{children}</div>
          )}
        </nav>

        {/* ── Footer CTA ── */}
        <div className="border-t border-slate-100 px-4 py-4">
          <Link
            href="/login"
            onClick={onClose}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98]"
          >
            <Icons.bot className="h-4 w-4" />
            Get Started Free
          </Link>
          <p className="mt-2 text-center text-[10px] text-slate-400">
            No credit card required
          </p>
        </div>
      </div>
    </>
  )
}